import React, { useState, useEffect } from 'react';
import { Loader2, X, Plus, Pencil, Trash2 } from 'lucide-react';
import { attendanceService } from '../../services/attendanceService';
import { employeeService } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import StatusBadge from '../../components/StatusBadge';

const todayStr = () => new Date().toISOString().split('T')[0];

const PERIODS = [
  { value: '', label: 'Specific Day' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom Range' },
];

const toStr = (d) => d.toISOString().split('T')[0];

function getDateRangeForPeriod(period, customFrom, customTo) {
  const now = new Date();
  if (period === 'weekly') {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { from: toStr(start), to: toStr(now) };
  }
  if (period === 'monthly') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toStr(start), to: toStr(now) };
  }
  if (period === 'yearly') {
    const start = new Date(now.getFullYear(), 0, 1);
    return { from: toStr(start), to: toStr(now) };
  }
  if (period === 'custom') {
    return { from: customFrom, to: customTo };
  }
  return { from: '', to: '' };
}

const STATUS_COLORS = {
  present: 'text-green-600',
  late: 'text-amber-500',
  half_day: 'text-blue-500',
  absent: 'text-red-500',
  on_leave: 'text-purple-500',
  day_off: 'text-gray-400'
};

const SHIFT_TYPES = ['Morning', 'Evening', 'Night', 'Flexible'];

// ===== Manual Override Modal =====
const ManualOverrideModal = ({ onClose, onDone }) => {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [mode, setMode] = useState('checkin');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await employeeService.getEmployees(undefined, 'active');
        setEmployees(res.data || []);
      } catch (err) {
        setError(err.message);
      }
    };
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!employeeId || !reason) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await attendanceService.manualOverride(employeeId, mode, reason);
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Manual Override</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Employee *</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.full_name + ' (' + emp.employee_id + ')'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mode *</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              <option value="checkin">Check In</option>
              <option value="checkout">Check Out</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="e.g. Face recognition failed after 3 attempts"
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Mark Attendance
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Schedule Modal (Create / Edit) =====
const ScheduleModal = ({ editingSchedule, departments, onClose, onDone }) => {
  const isEdit = !!editingSchedule;
  const [scheduleName, setScheduleName] = useState(isEdit ? editingSchedule.schedule_name : '');
  const [department, setDepartment] = useState(isEdit ? editingSchedule.department : '');
  const [shiftType, setShiftType] = useState(isEdit ? editingSchedule.shift_type : 'Morning');
  const [startTime, setStartTime] = useState(isEdit ? editingSchedule.start_time : '09:00');
  const [endTime, setEndTime] = useState(isEdit ? editingSchedule.end_time : '17:00');
  const [graceTime, setGraceTime] = useState(isEdit ? editingSchedule.grace_time : 10);
  const [requiredHours, setRequiredHours] = useState(isEdit ? editingSchedule.required_hours : 8.0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!scheduleName || !department) {
      setError('Please fill in all required fields.');
      return;
    }
    const payload = {
      schedule_name: scheduleName,
      department: department,
      shift_type: shiftType,
      start_time: startTime,
      end_time: endTime,
      grace_time: parseInt(graceTime),
      required_hours: parseFloat(requiredHours)
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await attendanceService.updateSchedule(editingSchedule.id, payload);
      } else {
        await attendanceService.createSchedule(payload);
      }
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Schedule' : 'New Schedule'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Schedule Name *</label>
              <input type="text" value={scheduleName} onChange={(e) => setScheduleName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Department *</label>
              <select value={department} onChange={(e) => setDepartment(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
                <option value="">Select department</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.department_name}>{d.department_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Shift Type</label>
              <select value={shiftType} onChange={(e) => setShiftType(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
                {SHIFT_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Grace Time (minutes)</label>
              <input type="number" value={graceTime} onChange={(e) => setGraceTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Time</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Time</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Required Hours</label>
              <input type="number" step="0.5" value={requiredHours} onChange={(e) => setRequiredHours(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Main Attendance Page =====
const Attendance = () => {
  const { user } = useAuth();
  const { notify, confirmDialog } = useNotification();
  const isAdmin = user && ['super_admin', 'hr_manager', 'finance_manager'].includes(user.role);
  const [activeTab, setActiveTab] = useState('register');
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [periodFilter, setPeriodFilter] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [records, setRecords] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [markingAbsent, setMarkingAbsent] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const loadRecords = async () => {
    setLoading(true);
    setError('');
    try {
      let res;
      if (periodFilter) {
        const { from, to } = getDateRangeForPeriod(periodFilter, customFrom, customTo);
        if (periodFilter === 'custom' && (!from || !to)) {
          setRecords([]);
          setLoading(false);
          return;
        }
        res = await attendanceService.getRange(from, to);
      } else {
        res = selectedDate === todayStr()
          ? await attendanceService.getToday()
          : await attendanceService.getByDate(selectedDate);
      }
      setRecords(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const res = await attendanceService.getSchedules();
      setSchedules(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const res = await employeeService.getDepartments();
      setDepartments(res.data || []);
    } catch (err) {
      // silent fail
    }
  };
useEffect(() => {
    loadRecords();
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, periodFilter, customFrom, customTo]);

  useEffect(() => {
    if (activeTab === 'schedules' && schedules.length === 0) {
      loadSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleMarkAbsent = async () => {
    const confirmed = await confirmDialog('Mark all employees with no check-in today as Absent?', { title: 'Mark Absentees', confirmLabel: 'Mark Absent' });
    if (!confirmed) return;
    setMarkingAbsent(true);
    try {
      const res = await attendanceService.markAbsentees();
      notify(res.message || 'Done', 'success');
      loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setMarkingAbsent(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const confirmed = await confirmDialog('Delete this schedule?', { title: 'Delete Schedule', confirmLabel: 'Delete' });
    if (!confirmed) return;
    setDeletingId(scheduleId);
    try {
      await attendanceService.deleteSchedule(scheduleId);
      loadSchedules();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId('');
    }
  };

  const presentCount = records.filter((r) => r.status === 'present' || r.status === 'late').length;
  const absentCount = records.filter((r) => r.status === 'absent').length;
  const lateCount = records.filter((r) => r.status === 'late').length;
  const leaveCount = records.filter((r) => r.status === 'on_leave').length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Attendance</h1>
          <p className="text-sm text-gray-500 mt-0.5">Daily register and work schedules</p>
        </div>
        {isAdmin && activeTab === 'register' && (
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <button onClick={handleMarkAbsent} disabled={markingAbsent || selectedDate !== todayStr()}
              className="px-4 py-2.5 rounded-control border border-gray-200 text-sm font-medium text-gray-600 hover:bg-surface-subtle disabled:opacity-50">
              {markingAbsent ? 'Marking...' : 'Mark Absentees'}
            </button>
            <button onClick={() => setShowOverrideModal(true)}
              className="px-4 py-2.5 rounded-control bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors">
              Manual Override
            </button>
          </div>
        )}
        {isAdmin && activeTab === 'schedules' && (
          <button
            onClick={() => { setEditingSchedule(null); setShowScheduleModal(true); }}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" />
            New Schedule
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-gray-100">
        <button onClick={() => setActiveTab('register')}
          className={'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
            (activeTab === 'register' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700')}>
          Register
        </button>
        {isAdmin && (
          <button onClick={() => setActiveTab('schedules')}
            className={'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
              (activeTab === 'schedules' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            Schedules
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>
      )}
{activeTab === 'register' && (
        <div>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}
              className="px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {!periodFilter && (
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} max="2099-12-31"
                className="px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            )}
            {periodFilter === 'custom' && (
              <>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} max="2099-12-31"
                  className="px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
                <span className="text-sm text-gray-400">to</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} max="2099-12-31"
                  className="px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
              </>
            )}
            <span className="text-sm text-gray-400">{records.length} records</span>
          </div>

          {records.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <div className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Present</p>
                <p className="text-xl font-semibold text-green-600 mt-1">{presentCount}</p>
              </div>
              <div className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Absent</p>
                <p className="text-xl font-semibold text-red-500 mt-1">{absentCount}</p>
              </div>
              <div className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Late</p>
                <p className="text-xl font-semibold text-amber-500 mt-1">{lateCount}</p>
              </div>
              <div className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">On Leave</p>
                <p className="text-xl font-semibold text-purple-500 mt-1">{leaveCount}</p>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {!loading && records.length === 0 && (
            <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
              <p className="text-sm text-gray-500">No attendance records for this date.</p>
            </div>
          )}

          {!loading && records.length > 0 && (
            <div>
              <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">
                      {periodFilter && <th className="px-5 py-3">Date</th>}
                      <th className="px-5 py-3">Employee</th>
                      <th className="px-5 py-3">Department</th>
                      <th className="px-5 py-3">Check In</th>
                      <th className="px-5 py-3">Check Out</th>
                      <th className="px-5 py-3">Hours</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-surface-subtle transition-colors">
                        {periodFilter && <td className="px-5 py-3.5 text-gray-600 whitespace-nowrap">{r.date}</td>}
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-800">{r.employee_name}</p>
                          <p className="text-xs text-gray-400">{r.employee_id}</p>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600">{r.department}</td>
                        <td className="px-5 py-3.5 text-gray-600">{r.check_in || '—'}</td>
                        <td className="px-5 py-3.5 text-gray-600">{r.check_out || '—'}</td>
                        <td className="px-5 py-3.5 text-gray-600">{r.work_hours ? r.work_hours + 'h' : '—'}</td>
                        <td className="px-5 py-3.5">
                          <span className={'text-xs font-medium capitalize ' + (STATUS_COLORS[r.status] || 'text-gray-600')}>
                            {r.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-gray-400">
                          {r.manual_override
                            ? <span title={r.override_reason || ''} className="text-amber-500 cursor-help">Manual</span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {records.map((r) => (
                  <div key={r.id} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{r.employee_name}</p>
                        <p className="text-xs text-gray-400">{r.employee_id}{periodFilter ? ' · ' + r.date : ''}</p>
                      </div>
                      <span className={'text-xs font-medium capitalize ' + (STATUS_COLORS[r.status] || 'text-gray-600')}>
                        {r.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                      <span>In: {r.check_in || '—'}</span>
                      <span>Out: {r.check_out || '—'}</span>
                      <span>{r.work_hours ? r.work_hours + 'h' : '—'}</span>
                    </div>
                    {r.manual_override && (
                      <p className="text-xs text-amber-500 mt-2">Manual: {r.override_reason}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'schedules' && (
        <div>
          {schedulesLoading && (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {!schedulesLoading && schedules.length === 0 && (
            <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
              <p className="text-sm text-gray-500">No schedules yet. Create one to define shift timings per department.</p>
            </div>
          )}

          {!schedulesLoading && schedules.length > 0 && (
            <div>
              <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Department</th>
                      <th className="px-5 py-3">Shift</th>
                      <th className="px-5 py-3">Start</th>
                      <th className="px-5 py-3">End</th>
                      <th className="px-5 py-3">Grace</th>
                      <th className="px-5 py-3">Req. Hours</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((s) => (
                      <tr key={s.id} className="border-b border-gray-50 last:border-0 hover:bg-surface-subtle transition-colors">
                        <td className="px-5 py-3.5 font-medium text-gray-800">{s.schedule_name}</td>
                        <td className="px-5 py-3.5 text-gray-600">{s.department}</td>
                        <td className="px-5 py-3.5 text-gray-600">{s.shift_type}</td>
                        <td className="px-5 py-3.5 text-gray-600">{s.start_time}</td>
                        <td className="px-5 py-3.5 text-gray-600">{s.end_time}</td>
                        <td className="px-5 py-3.5 text-gray-600">{s.grace_time + ' min'}</td>
                        <td className="px-5 py-3.5 text-gray-600">{s.required_hours + 'h'}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-3">
                            <button onClick={() => { setEditingSchedule(s); setShowScheduleModal(true); }}
                              className="text-gray-400 hover:text-primary-light">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteSchedule(s.id)}
                              disabled={deletingId === s.id}
                              className="text-gray-400 hover:text-red-500 disabled:opacity-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {schedules.map((s) => (
                  <div key={s.id} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{s.schedule_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{s.department} &middot; {s.shift_type}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                      <span>{s.start_time} — {s.end_time}</span>
                      <span>Grace: {s.grace_time}min</span>
                      <span>{s.required_hours}h</span>
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
                      <button onClick={() => { setEditingSchedule(s); setShowScheduleModal(true); }}
                        className="flex items-center gap-1 text-xs font-medium text-primary-light">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => handleDeleteSchedule(s.id)}
                        disabled={deletingId === s.id}
                        className="flex items-center gap-1 text-xs font-medium text-red-500 disabled:opacity-50">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showOverrideModal && (
        <ManualOverrideModal
          onClose={() => setShowOverrideModal(false)}
          onDone={() => { setShowOverrideModal(false); loadRecords(selectedDate); }}
        />
      )}

      {showScheduleModal && (
        <ScheduleModal
          editingSchedule={editingSchedule}
          departments={departments}
          onClose={() => { setShowScheduleModal(false); setEditingSchedule(null); }}
          onDone={() => { setShowScheduleModal(false); setEditingSchedule(null); loadSchedules(); }}
        />
      )}
    </div>
  );
};

export default Attendance;